'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Language, LanguageContextType, TranslationKeys } from '@/types/i18n';
import { isValidLanguage, getDefaultLanguage, STORAGE_KEYS } from '@/lib/i18n-utils';

// Import translation files
import enTranslations from '@/lib/translations/en.json';
import frTranslations from '@/lib/translations/fr.json';

const translations: Record<Language, TranslationKeys> = {
  en: enTranslations as TranslationKeys,
  fr: frTranslations as TranslationKeys,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [language, setLanguageState] = useState<Language>(getDefaultLanguage());
  const [isLoading, setIsLoading] = useState(false);
  const [showLanguageSelection, setShowLanguageSelection] = useState(false);
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [transitionPhase, setTransitionPhase] = useState<'idle' | 'loading' | 'switching' | 'completing'>('idle');
  const [animationsFrozen, setAnimationsFrozen] = useState(false);
  const [isInAppLanguageChange, setIsInAppLanguageChange] = useState(false);

  // Initialize language state from localStorage or detect first visit
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE_PREFERENCE) as Language | null;
        const hasVisited = localStorage.getItem(STORAGE_KEYS.FIRST_VISIT);
        
        // Check if we're on a language-specific page
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const urlLanguage = pathname.split('/')[1];
        const isOnLanguagePage = isValidLanguage(urlLanguage);

        if (savedLanguage && isValidLanguage(savedLanguage)) {
          // User has a saved preference
          setLanguageState(savedLanguage);
          setHasSelectedLanguage(true);
          setIsFirstVisit(false);
          setShowLanguageSelection(false);
        } else if (isOnLanguagePage) {
          // User is on a language-specific page (first visit after selection)
          setLanguageState(urlLanguage as Language);
          setHasSelectedLanguage(true);
          setIsFirstVisit(false);
          setShowLanguageSelection(false);
        } else if (!hasVisited) {
          // First-time visitor
          setIsFirstVisit(true);
          setShowLanguageSelection(false); // Will be shown by root page
          setHasSelectedLanguage(false);
        } else {
          // Returning visitor without language preference (edge case)
          setLanguageState(getDefaultLanguage());
          setHasSelectedLanguage(false);
          setIsFirstVisit(false);
          setShowLanguageSelection(false); // Will be shown by root page
        }
      } catch (error) {
        console.warn('Error accessing localStorage:', error);
        // Fallback to default language if localStorage is not available
        setLanguageState(getDefaultLanguage());
        setHasSelectedLanguage(false);
        setIsFirstVisit(true);
        setShowLanguageSelection(false); // Will be shown by root page
      }

      setIsLoading(false);
    };

    initializeLanguage();
  }, []);

  // Function to update language state without navigation (for URL-driven changes)
  const updateLanguageState = (newLanguage: Language) => {
    // Always update hasSelectedLanguage to true when explicitly setting language from URL
    // This ensures the language toggle appears even if the language hasn't changed
    setHasSelectedLanguage(true);
    setShowLanguageSelection(false);
    setIsFirstVisit(false);
    
    // Only update language state if it's actually different
    if (newLanguage === language) return;
    
    setLanguageState(newLanguage);
  };

  // Function to change language with seamless, continuous transitions
  const setLanguage = (newLanguage: Language, skipNavigation = false) => {
    if (newLanguage === language) return; // No change needed

    // Check if this is a first-time language selection
    const isFirstTimeSelection = isFirstVisit && !hasSelectedLanguage;
    
    // Set flag for in-app language changes (not first-time selections)
    setIsInAppLanguageChange(!isFirstTimeSelection);

    // For first-time selections, we want to redirect immediately without progress bar
    if (isFirstTimeSelection) {
      try {
        // Save to localStorage
        localStorage.setItem(STORAGE_KEYS.LANGUAGE_PREFERENCE, newLanguage);
        // Also save for dashboard integration
        localStorage.setItem(STORAGE_KEYS.DASHBOARD_LANGUAGE, newLanguage);
        // Mark as visited
        localStorage.setItem(STORAGE_KEYS.FIRST_VISIT, 'true');
      } catch (error) {
        console.warn('Error saving language preference:', error);
      }

      // Update state immediately
      setLanguageState(newLanguage);
      setHasSelectedLanguage(true);
      setShowLanguageSelection(false);
      setIsFirstVisit(false);

      // Handle URL-based routing with Next.js router (only if not skipping navigation)
      if (!skipNavigation && typeof window !== 'undefined') {
        const currentHash = window.location.hash;
        const currentSearch = window.location.search;

        // Navigate to new URL with preserved hash and search params
        const newPath = `/${newLanguage}`;
        const newUrl = `${newPath}${currentSearch}${currentHash}`;
        router.replace(newUrl);
      }
      return;
    }

    // For in-app language changes, show progress bar and use smooth transitions
    // Preserve scroll position without scrolling during transition
    const scrollPosition = typeof window !== 'undefined' ? window.scrollY : 0;

    // Preserve form data by storing it temporarily
    const preservedFormData = new Map<string, FormData>();
    if (typeof window !== 'undefined') {
      const forms = document.querySelectorAll('form');
      forms.forEach((form, index) => {
        const formData = new FormData(form);
        if (formData.entries().next().value) { // Only store if form has data
          preservedFormData.set(`form-${index}`, formData);
        }
      });
    }

    // Phase 1: Start loading immediately and freeze animations
    setIsLoading(true);
    setTransitionPhase('loading');
    setAnimationsFrozen(true);

    // Phase 2: Switch language at 50% progress (800ms)
    setTimeout(() => {
      setTransitionPhase('switching');
      try {
        // Save to localStorage
        localStorage.setItem(STORAGE_KEYS.LANGUAGE_PREFERENCE, newLanguage);
        // Also save for dashboard integration
        localStorage.setItem(STORAGE_KEYS.DASHBOARD_LANGUAGE, newLanguage);

        // Preserve scroll position throughout the entire transition
        if (typeof window !== 'undefined') {
          // Store the scroll position in a way that persists through navigation
          sessionStorage.setItem('language-switch-scroll', scrollPosition.toString());

          // Immediately set scroll position to prevent any jumping
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        }

        // Update state while overlay is visible
        setLanguageState(newLanguage);
        setHasSelectedLanguage(true);
        setShowLanguageSelection(false);
        setIsFirstVisit(false);

        // Handle URL-based routing with Next.js router (only if not skipping navigation)
        if (!skipNavigation && typeof window !== 'undefined') {
          const currentHash = window.location.hash;
          const currentSearch = window.location.search;

          // Determine new path based on current URL structure
          let newPath = `/${newLanguage}`;

          // If current path has language prefix, replace it
          if (pathname.startsWith('/en') || pathname.startsWith('/fr') || pathname === '/') {
            if (pathname.startsWith('/en') || pathname.startsWith('/fr')) {
              const pathWithoutLang = pathname.substring(3); // Remove '/en' or '/fr'
              newPath = `/${newLanguage}${pathWithoutLang}`;
            } else {
              newPath = `/${newLanguage}`;
            }
          } else {
            // If current path doesn't have language prefix but isn't root, add it
            newPath = `/${newLanguage}${pathname}`;
          }

          // Navigate to new URL with preserved hash and search params
          const newUrl = `${newPath}${currentSearch}${currentHash}`;
          router.push(newUrl);
        }
      } catch (error) {
        console.warn('Error saving language preference:', error);
      }
    }, 800);

    // Phase 3: Complete transition (1000ms)
    setTimeout(() => {
      setTransitionPhase('completing');

      // Restore form data
      if (typeof window !== 'undefined' && preservedFormData.size > 0) {
        const forms = document.querySelectorAll('form');
        forms.forEach((form, index) => {
          const formData = preservedFormData.get(`form-${index}`);
          if (formData) {
            for (const [key, value] of formData.entries()) {
              const input = form.querySelector(`[name="${key}"]`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
              if (input) {
                if (input.type === 'checkbox' || input.type === 'radio') {
                  (input as HTMLInputElement).checked = value === 'on';
                } else {
                  input.value = value as string;
                }
              }
            }
          }
        });
      }
    }, 1000);

    // Phase 4: End transition with smooth fade out (1600ms total)
    setTimeout(() => {
      // Start fade out animation
      setTransitionPhase('idle');

      // Actually end loading and unfreeze animations after fade out animation completes
      setTimeout(() => {
        setIsLoading(false);
        setAnimationsFrozen(false); // Unfreeze animations after transition is complete
        setIsInAppLanguageChange(false); // Reset flag
      }, 400); // Allow time for smooth fade out
    }, 1600);
  };

  // Translation function with fallback mechanism
  const t = (key: string): string => {
    try {
      const keys = key.split('.');
      let value: any = translations[language];

      // Navigate through nested keys
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Key not found, try English fallback
          value = translations.en;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object' && fallbackKey in value) {
              value = value[fallbackKey];
            } else {
              // Even English fallback failed, return the key itself
              console.warn(`Translation key not found: ${key}`);
              return key;
            }
          }
          break;
        }
      }

      // Return the found value or the key if not found
      return typeof value === 'string' ? value : key;
    } catch (error) {
      console.warn(`Error translating key "${key}":`, error);
      return key;
    }
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    updateLanguageState,
    t,
    isLoading,
    showLanguageSelection,
    hasSelectedLanguage,
    isFirstVisit,
    transitionPhase,
    animationsFrozen,
    isInAppLanguageChange,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to use the language context
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Export the context for advanced use cases
export { LanguageContext };